import { useMutation } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { candidateBriefQueryKeys } from '@/modules/candidate-detail/queries/queryKeys';
import { candidateEndpoints } from '@/modules/candidates/constants';
import {
  candidateApprovalMessageQueryKeys,
  candidateJobQueryKeys,
} from '@/modules/candidates/queries/queryKeys';
import { candidateSubmissionsAwaitingReviewQueryKeys } from '@/modules/hiring-manager/queries/useCandidateSubmissionsAwaitingReviewQuery';
import { invalidateQuery } from '@/utils/queries';

interface ApproveCandidateJobParams {
  jobId: string;
  candidateId: string;
}

/**
 * Approves a submitted candidate on a self-service job. Resolves to whether
 * the approval message was queued; the approval stands either way.
 */
export const useApproveCandidateJobMutation = () =>
  useMutation({
    mutationFn: async ({ jobId, candidateId }: ApproveCandidateJobParams) => {
      const { data } = await fetchClient.POST(
        candidateEndpoints.approveCandidate,
        { params: { path: { jobId, candidateId } } },
      );
      return data?.messaged ?? false;
    },
    onSettled: (_data, _error, { jobId, candidateId }) => {
      const params = { jobId, candidateId };
      // The brief key prefixes its touchpoints, so the activity refreshes too.
      invalidateQuery(candidateBriefQueryKeys.brief(params))();
      invalidateQuery(candidateJobQueryKeys.lists(jobId))();
      invalidateQuery(
        candidateApprovalMessageQueryKeys.detail(jobId, candidateId),
      )();
      invalidateQuery(candidateSubmissionsAwaitingReviewQueryKeys.all)();
    },
  });
